import { useCallback, useContext } from 'react';

import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';
import type { PromoCarouselLocation } from '~/domains/promotions/types';

import { MissionStatus, type MissionAssignment } from '../types/missions.types';
import {
  reportMissionCompletedOpened,
  reportMissionDetailOpened,
} from '../analyticsEvents';

/**
 * Opens the status-appropriate mission detail modal. Passes only `assignmentId`;
 * the Connected modal fetches full detail (incl. rewards) via useGetMissionDetail.
 *
 * `location` attributes the resulting analytics event to the surface the card was clicked on.
 */
export const useMissionModals = (location: PromoCarouselLocation) => {
  const { openModalUnique } = useContext(PromotionsModalsContext);

  const openMissionDetailModal = useCallback(
    (missionAssignment: MissionAssignment) => {
      const { assignmentId, status } = missionAssignment;

      if (status === MissionStatus.COMPLETED) {
        reportMissionCompletedOpened({ missionAssignment, location });
        openModalUnique({ type: 'MissionCompletedModal', isClosing: true, assignmentId });
      } else if (status === MissionStatus.STARTED) {
        reportMissionDetailOpened({ missionAssignment, location });
        openModalUnique({ type: 'MissionStartedModal', isClosing: true, missionAssignment });
      }
    },
    [openModalUnique, location],
  );

  return { openMissionDetailModal };
};
